import {
  ArrowDownIcon,
  ArrowsRightLeftIcon,
  ArrowUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { TextHighlighter } from 'components/TextHighlighter'
import { accountTypeWithBorrower } from 'config/user.type.constants'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { impersonateSearch, impersonateUser } from 'services'
import { applyImpersonationSession } from 'utils/applyImpersonationSession'
import { redirectAfterImpersonationIfNeeded } from 'utils/impersonationNavigation'

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export const GlobalUserSwitch = ({ className = 'w-28 lg:w-32' }: { className?: string }) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const history = useHistory()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [users, setUsers] = useState<Record<string, any>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const collapse = () => {
    setIsExpanded(false)
    setIsOpen(false)
    setQuery('')
    setDebouncedQuery('')
    setUsers([])
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedQuery(query.trim()), 400)
    return () => clearTimeout(timeoutId)
  }, [query])

  useEffect(() => {
    setActiveIndex(0)
  }, [debouncedQuery, users])

  useEffect(() => {
    if (!debouncedQuery) {
      setUsers([])
      setTotal(0)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    impersonateSearch(debouncedQuery)
      .then((res) => {
        if (cancelled) return
        setUsers(res.users || [])
        setTotal(Number(res.total) || 0)
      })
      .catch(() => {
        if (!cancelled) {
          setUsers([])
          setTotal(0)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
        if (!query.trim()) collapse()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [query])

  const onSelectUser = async (user: Record<string, any>) => {
    if (switching) return
    setSwitching(true)

    try {
      const response = await impersonateUser(user.value || user.email, !!user.isBorrower)
      collapse()
      await applyImpersonationSession(dispatch, queryClient, response)
      redirectAfterImpersonationIfNeeded(history, response.user)
    } catch {
      toast.error('Unable to switch user')
    } finally {
      setSwitching(false)
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !query.trim()) {
      if (event.key === 'Escape') collapse()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!users.length) return
      setActiveIndex((prev) => Math.min(prev + 1, users.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!users.length) return
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (users[activeIndex]) onSelectUser(users[activeIndex])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      collapse()
    }
  }

  const showResults = isExpanded && isOpen && query.trim().length > 0

  return (
    <div className={`GlobalUserSwitch-container relative ${isExpanded ? className : ''}`} ref={containerRef}>
      {!isExpanded ? (
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-slate-100 hover:text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
          aria-label="Switch user"
          title="Switch user"
          disabled={switching}
          onClick={() => {
            setIsExpanded(true)
            setTimeout(() => inputRef.current?.focus(), 0)
          }}
        >
          <ArrowsRightLeftIcon className="h-4 w-4" />
        </button>
      ) : (
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Switch user…"
            value={query}
            className="block w-full rounded-full border-0 bg-slate-100 py-1.5 pl-8 pr-8 text-xs text-gray-800 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-200 [&::-webkit-search-cancel-button]:hidden"
            onChange={(event) => {
              setQuery(event.target.value)
              setIsOpen(true)
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={onKeyDown}
            disabled={switching}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            onMouseDown={(event) => {
              event.preventDefault()
              collapse()
            }}
            aria-label="Close switch user"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {showResults && (
        <div className="absolute right-0 z-[60] mt-2 w-[min(24rem,calc(100vw-2rem))]">
          <div className="relative overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-2xl">
            <LayoutLoading show={loading || switching} />
            {!loading && !users.length && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">No users found.</div>
            )}
            {!!users.length && (
              <ul className="max-h-72 overflow-y-auto p-1.5">
                {users.map((user, index) => {
                  const isActive = index === activeIndex
                  const name = user.name || user.label || user.value
                  const subtitle = [user.isBorrower ? '' : user.companyName, user.email || user.value]
                    .filter(Boolean)
                    .join(' · ')
                  const typeLabel = accountTypeWithBorrower()[user.accountType] || user.accountType

                  return (
                    <li key={user.id || user.value || index}>
                      <button
                        type="button"
                        className={`group w-full rounded-lg px-2.5 py-2 text-left transition-colors focus:outline-none ${
                          isActive ? 'bg-sky-50' : 'hover:bg-gray-50'
                        }`}
                        onMouseDown={(event) => {
                          event.preventDefault()
                          onSelectUser(user)
                        }}
                        onMouseEnter={() => setActiveIndex(index)}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[11px] font-semibold text-sky-700">
                            {getInitials(name)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="truncate text-sm font-semibold text-gray-900">
                                <TextHighlighter searchText={query} text={name} />
                              </div>
                              {!!typeLabel && (
                                <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                                  {typeLabel}
                                </span>
                              )}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              <TextHighlighter searchText={query} text={subtitle} />
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
            <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-[11px] text-gray-400">
              <div className="flex items-center gap-2">
                <ArrowUpIcon className="h-3 w-3" />
                <ArrowDownIcon className="h-3 w-3" />
                <span>to navigate</span>
              </div>
              {!!users.length && (
                <span>
                  {activeIndex + 1} of {Math.min(users.length, total || users.length)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
